export class Schedule {
    constructor(schedule) {
        var _a;
        this.dailySchedule = {};
        this.fmt = Intl.DateTimeFormat("en", {
            timeZone: schedule.timeZone,
            timeStyle: "short",
            dateStyle: "short",
        });
        this.defaultOpen = !((_a = schedule.schedule.open) === null || _a === void 0 ? void 0 : _a.length);
        this.parse(schedule);
    }
    /**
     * @param year
     * @param month 0-11
     *
     * @returns array where each index is a day of the month\
     *          if closed, or containing closed intervals, [reasons] might be given
     */
    getDays(year, month) {
        // get the days of a month
        const days = new Date(year, month + 1, 0).getDate();
        return Array(days)
            .fill(null)
            .map((_, day) => this.getDaySchedule(year, month, day + 1).reduce((acc, cur) => {
            var _a;
            acc.open || (acc.open = cur.open);
            if ((_a = cur.origin) === null || _a === void 0 ? void 0 : _a.reason) {
                acc.reasons.push(cur.origin.reason);
            }
            return acc;
        }, {
            open: false,
            reasons: [],
        }));
    }
    /**
     * @param year
     * @param month 0-11
     * @param day 1-31
     *
     * @returns array where each index is an hour of the day, 0-23\
     *          if closed, or containing closed intervals, [reasons] might be given
     */
    getHours(year, month, day) {
        var _a;
        var _b;
        const hours = Array(24)
            .fill(null)
            .map(() => ({
            open: false,
            reasons: [],
        }));
        for (const interval of this.getDaySchedule(year, month, day)) {
            const start = Math.trunc(interval.from / 100);
            const end = Math.trunc(interval.to / 100) + (interval.to % 100 ? 1 : 0);
            for (let i = start; i < end; i++) {
                (_b = hours[i]).open || (_b.open = interval.open);
                if ((_a = interval.origin) === null || _a === void 0 ? void 0 : _a.reason) {
                    hours[i].reasons.push(interval.origin.reason);
                }
            }
        }
        return hours;
    }
    /**
     * @param year
     * @param month 0-11
     * @param day 1-31
     * @param hour 0-23
     *
     * @returns array where each index is an hour of the day, 0-23\
     *          if closed, or containing closed intervals, [reasons] might be given
     */
    getMinutes(year, month, day, hour) {
        const start = hour * 100;
        const end = (hour + 1) * 100;
        return this.getDaySchedule(year, month, day)
            .filter((interval) => start < interval.to && interval.from < end)
            .flatMap((interval) => {
            var _a;
            return Array(Math.min(start + 60, interval.to) - Math.max(start, interval.from)).fill({
                open: interval.open,
                reasons: ((_a = interval.origin) === null || _a === void 0 ? void 0 : _a.reason) ? [interval.origin.reason] : [],
            });
        });
    }
    /**
     * Return next open day of the month after [day] included
     *
     * @param year
     * @param month 0-11
     * @param day from this day included 1-31
     *
     * @returns day or null if none is open
     */
    nextOpenDay(year, month, day = 1) {
        // get the days of a month
        const days = new Date(year, month + 1, 0).getDate();
        for (; day <= days; day++) {
            if (this.getDaySchedule(year, month, day).some((interval) => interval.open)) {
                return day;
            }
        }
        return null;
    }
    /**
     * Return next open time of the day, by [step] offset, after [time] included
     * @param year
     * @param month 0-11
     * @param day 1-31
     * @param hour from this hour included 0-23
     * @param minute from this minute included 0-59
     * @param step 1-60 minimum time increments in minutes to consider
     *
     * @returns hour or null if none open
     */
    nextOpenTime(year, month, day, hour = 0, minute = 0, step = 1) {
        let failsafe = (24 * 60) / step;
        let time = hour * 100 + minute;
        for (const interval of this.getDaySchedule(year, month, day)) {
            if (time < interval.to && interval.open) {
                return {
                    hour,
                    minute,
                };
            }
            while (time < interval.to && failsafe--) {
                hour += Math.trunc((minute + step) / 60);
                minute = (minute + step) % 60;
                time = hour * 100 + minute;
            }
        }
        return null;
    }
    /**
     * Return previous open time of the day, by [step] offset, before [time] excluded
     * @param year
     * @param month 0-11
     * @param day 1-31
     * @param hour from this hour included 0-23
     * @param minute from this minute excluded 0-59
     * @param step 1-60 minimum time increments in minutes to consider
     *
     * @returns hour or null if none open
     */
    previousOpenTime(year, month, day, hour = 0, minute = 0, step = 1) {
        let failsafe = (24 * 60) / step;
        hour -= Math.trunc((59 - (minute - step)) / 60);
        minute = (60 + minute - step) % 60;
        let time = hour * 100 + minute;
        const intervals = this.getDaySchedule(year, month, day);
        for (let i = intervals.length - 1; i >= 0; i--) {
            const interval = intervals[i];
            if (time >= interval.from && interval.open) {
                return {
                    hour,
                    minute,
                };
            }
            while (time >= interval.from && failsafe--) {
                hour -= Math.trunc((59 - (minute - step)) / 60);
                minute = (60 + minute - step) % 60;
                time = hour * 100 + minute;
            }
        }
        return null;
    }
    /** clear internal data */
    clear() {
        this.dailySchedule = {};
    }
    // make sure number is in between acceptable range
    minmax(number, min, max, invalid = NaN) {
        return Number.isInteger(number)
            ? number > min
                ? number < max
                    ? number
                    : max
                : min
            : invalid;
    }
    parse(schedule) {
        var _a, _b;
        this.timeZone = schedule.timeZone;
        this.open =
            ((_a = schedule.schedule.open) === null || _a === void 0 ? void 0 : _a.flatMap((entry) => this.parseScheduleRecord(entry))) || [];
        this.closed =
            ((_b = schedule.schedule.closed) === null || _b === void 0 ? void 0 : _b.flatMap((entry) => this.parseScheduleRecord(entry))) || [];
    }
    parseScheduleRecord(entry) {
        const dates = Array.isArray(entry.date)
            ? entry.date.map((date) => ({ date }))
            : [
                {
                    date: typeof entry.date === "string" ? entry.date : "1-1/12-31",
                },
            ];
        const weekdays = Array.isArray(entry.weekdays)
            ? entry.weekdays.map((w) => this.minmax(w - 1, 0, 6))
            : typeof entry.weekdays === "number"
                ? [this.minmax(entry.weekdays - 1, 0, 6)]
                : undefined;
        const times = Array.isArray(entry.time)
            ? entry.time.map((time) => ({ time }))
            : [
                {
                    time: typeof entry.time === "string" ? entry.time : "00:00/24:00",
                },
            ];
        const merger = (a, b) => (Object.assign(Object.assign(Object.assign({}, a), b), { origin: entry }));
        const records = this.cartesianProduct(merger, dates, times);
        return records
            .map((record) => {
            const date = this.parseDates(record.date);
            const time = this.parseTimes(record.time);
            return {
                weekdays,
                origin: record.origin,
                from: { date: date.from, time: time.from },
                to: { date: date.to, time: time.to },
            };
        })
            .sort((a, b) => a.from.time - b.from.time);
    }
    cartesianProduct(merger, a, b) {
        return a.flatMap((a) => b.map((b) => merger(a, b)));
    }
    parseTimes(times) {
        const [from, to] = times.split("/");
        return {
            from: this.parseTime(from),
            to: this.parseTime(to),
        };
    }
    parseTime(time) {
        const [hour, minute] = time.split(":");
        return (this.minmax(parseInt(hour), 0, 24) * 100 +
            this.minmax(parseInt(minute), 0, 59));
    }
    parseDates(date) {
        const dates = date.split("/");
        let [year, month, day] = this.parseDate(dates[0]);
        if (!year) {
            year = 0;
        }
        const from = year * 10000 + month * 100 + day;
        if (dates[1]) {
            [year, month, day] = this.parseDate(dates[1]);
        }
        if (!year) {
            year = 0;
        }
        const to = year * 10000 + month * 100 + day;
        return { from, to };
    }
    parseDate(date) {
        const [day, month, year] = date.split("-").reverse();
        return [
            parseInt(year),
            this.minmax(parseInt(month) - 1, 0, 11),
            this.minmax(parseInt(day), 1, 31),
        ];
    }
    shiftTimeZone(date) {
        const diff = new Date(this.fmt.format(date)).getTime() - date.getTime();
        date.setMinutes(date.getMinutes() + Math.floor(diff / 60000));
        return date;
    }
    getDaySchedule(year, month, day) {
        const index = year * 10000 + month * 100 + day;
        if (this.dailySchedule[index]) {
            return this.dailySchedule[index];
        }
        let date = this.shiftTimeZone(new Date(year, month, day, 0, 0));
        const from = {
            date: date.getFullYear() * 10000 + date.getMonth() * 100 + date.getDate(),
            weekday: date.getDay(),
            time: date.getHours() * 100 + date.getMinutes(),
        };
        // next day
        date = this.shiftTimeZone(new Date(year, month, day + 1, 0, 0));
        const to = {
            date: date.getFullYear() * 10000 + date.getMonth() * 100 + date.getDate(),
            weekday: date.getDay(),
            time: date.getHours() * 100 + date.getMinutes(),
        };
        let intervals = this.searchIntervals({
            from,
            to: {
                date: from.date,
                weekday: from.weekday,
                time: 24 * 100 + 0,
            },
        });
        // minutes from 00:00
        const toMinutes = (n) => Math.trunc(n / 100) * 60 + (n % 100);
        const fromMinutes = (n) => Math.trunc(n / 60) * 100 + (n % 60);
        const reAdjust = (diff, offset, intervals) => {
            if (diff) {
                for (const interval of intervals) {
                    interval.from.time = offset;
                    offset = interval.to.time = fromMinutes((toMinutes(interval.to.time) + diff) % 1440);
                }
            }
        };
        // +/- timezone difference in minutes of interval's beginning
        reAdjust((from.date % 100 < day ? 24 * 60 : 0) - toMinutes(from.time), 0, intervals);
        if (to.time !== 0) {
            const ints = this.searchIntervals({
                from: {
                    date: to.date,
                    weekday: to.weekday,
                    time: 0,
                },
                to,
            });
            // calculate difference from end, maybe minimize dst error
            reAdjust((to.date % 100 === day ? 24 * 60 : 0) - toMinutes(to.time), intervals[intervals.length - 1].to.time, ints);
            intervals = intervals.concat(ints);
        }
        // close the day
        intervals[intervals.length - 1].to.time = 2400;
        return (this.dailySchedule[index] = intervals.map((interval) => ({
            from: interval.from.time,
            to: interval.to.time,
            open: interval.open,
            origin: interval.origin,
            weekdays: interval.weekdays,
        })));
    }
    searchIntervals(query) {
        const areDatesContained = (dates, range) => (range.from.date < 1300 ? dates.from.date % 10000 : dates.from.date) >=
            range.from.date &&
            (range.to.date < 1300 ? dates.to.date % 10000 : dates.to.date) <=
                range.to.date;
        // needs sorting as it may include more than one entries
        const closed = this.closed
            .filter((record) => (Array.isArray(record.weekdays)
            ? record.weekdays.includes(query.from.weekday)
            : true) && areDatesContained(query, record))
            .sort((a, b) => a.from.time - b.from.time);
        // lock a single record
        const openDate = this.open.find((record) => (Array.isArray(record.weekdays)
            ? record.weekdays.includes(query.from.weekday)
            : true) && areDatesContained(query, record));
        // get all intervals of that record
        const open = openDate
            ? this.open.filter((record) => record.origin === openDate.origin)
            : [];
        const intervals = this.searchInterval(query, closed);
        return intervals.flatMap((interval) => interval.origin
            ? Object.assign(Object.assign({}, interval), { open: false }) : this.searchInterval(interval, open).map((interval) => (Object.assign(Object.assign({}, interval), { open: !!interval.origin || this.defaultOpen }))));
    }
    searchInterval(query, records) {
        const intervals = [];
        const from = Object.assign({}, query.from);
        const to = Object.assign({}, query.to);
        for (let i = 0; i < records.length && from.time < to.time; i++) {
            const interval = records[i];
            let inside;
            let outside;
            // no overlap
            if (to.time <= interval.from.time) {
                break;
            }
            else if (interval.to.time <= from.time) {
                continue;
            }
            // type of relative position
            const type = +(to.time < interval.to.time) | (+(from.time < interval.from.time) * 2);
            switch (type) {
                case 0b00:
                    inside = {
                        from: Object.assign(Object.assign({}, from), { time: from.time }),
                        to: Object.assign(Object.assign({}, to), { time: interval.to.time }),
                        origin: interval.origin,
                    };
                    break;
                case 0b01:
                    inside = {
                        from: Object.assign(Object.assign({}, from), { time: from.time }),
                        to: Object.assign(Object.assign({}, to), { time: to.time }),
                        origin: interval.origin,
                    };
                    break;
                case 0b10:
                    outside = {
                        from: Object.assign(Object.assign({}, from), { time: from.time }),
                        to: Object.assign(Object.assign({}, to), { time: interval.from.time }),
                    };
                    inside = {
                        from: Object.assign(Object.assign({}, from), { time: interval.from.time }),
                        to: Object.assign(Object.assign({}, to), { time: interval.to.time }),
                        origin: interval.origin,
                    };
                    break;
                case 0b11:
                    outside = {
                        from: Object.assign(Object.assign({}, from), { time: from.time }),
                        to: Object.assign(Object.assign({}, to), { time: interval.from.time }),
                    };
                    inside = {
                        from: Object.assign(Object.assign({}, from), { time: interval.from.time }),
                        to: Object.assign(Object.assign({}, to), { time: to.time }),
                        origin: interval.origin,
                    };
                    break;
            }
            if (outside) {
                intervals.push(outside);
                from.time = outside.to.time;
            }
            if (inside) {
                intervals.push(inside);
                from.time = inside.to.time;
            }
        }
        if (from.time < to.time) {
            intervals.push({
                from: Object.assign(Object.assign({}, from), { time: from.time }),
                to: Object.assign(Object.assign({}, to), { time: to.time }),
            });
        }
        return intervals;
    }
}
//# sourceMappingURL=schedule.js.map